import { ReactElement, useMemo, useCallback } from 'react'
import { Box, Card, Chip, Skeleton, Typography } from '@mui/material'
import { DataGridPremium } from '@mui/x-data-grid-premium'
import DataGridToolbar from 'src/v3/components/data-grid-toolbar'
import { dataGridSx } from '../../styles'
import {
  courseColumns,
  DATAGRID_DEFAULT_PAGE_SIZE,
  DATAGRID_PAGE_SIZE_OPTIONS,
} from '../../constants'
import type { CourseRow } from '../utils/course-transforms'

type TrainingSeriesCoursesCardProps = {
  courses: CourseRow[]
  isLoading: boolean
}

export default function TrainingSeriesCoursesCard({
  courses,
  isLoading,
}: TrainingSeriesCoursesCardProps): ReactElement {
  let TrainingSeriesCoursesToolbar = useCallback(
    () => <DataGridToolbar fileName="training-series-courses" />,
    [],
  )

  let stats = useMemo(() => {
    let totalHours = courses.reduce((sum, course) => sum + course.length, 0)

    return {
      courseCount: courses.length,
      totalHours,
    }
  }, [courses])

  return (
    <Card className="p-6 mb-6">
      <Box className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <Typography variant="h6" component="h2">
          Courses
        </Typography>
        <Box className="flex items-center gap-2">
          <Chip
            label={`${stats.courseCount} course${stats.courseCount !== 1 ? 's' : ''}`}
            size="medium"
            color="primary"
          />
          <Chip
            label={`${stats.totalHours.toFixed(1)} hours`}
            size="medium"
            color="primary"
          />
        </Box>
      </Box>

      {isLoading ? (
        <Skeleton
          variant="rectangular"
          height={400}
          sx={{ borderRadius: 1 }}
          aria-label="Loading courses"
        />
      ) : (
        <DataGridPremium
          rows={courses}
          columns={courseColumns}
          slots={{ toolbar: TrainingSeriesCoursesToolbar }}
          showToolbar
          initialState={{
            pagination: {
              paginationModel: { pageSize: DATAGRID_DEFAULT_PAGE_SIZE },
            },
          }}
          disableRowSelectionOnClick
          pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
          sx={dataGridSx}
          aria-label="Training series courses"
        />
      )}
    </Card>
  )
}
